import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Shield, Layers } from "lucide-react";
import GroupMultiSelect from "./GroupMultiSelect";

// Pacote de Correções, item 5 — Grupos autorizados do Supervisor deixou de
// ter um botão/dialog separado ("Grupos", ao lado de "Permissões") e passou
// a viver DENTRO deste dialog, numa seção própria: um único lugar reúne
// Permissões funcionais + Grupos autorizados. A lógica/validação de
// autorização por Grupo no backend não mudou — só a organização da tela
// (group_ids continua sendo salvo via PUT /users/{id}, e o backend segue
// ignorando group_ids para perfis diferentes de supervisor).
export default function PermissionsDialog({ user, groups = [], open, onOpenChange, onSaved }) {
  const [meta, setMeta] = useState(null);
  const [selected, setSelected] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const isSupervisor = user?.role === "supervisor";

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data } = await api.get("/permissions");
      setMeta(data);
      setSelected(user?.permissions || []);
      setSelectedGroups(user?.group_ids || []);
    })();
  }, [open, user]);

  const toggle = (p) => setSelected(s => s.includes(p) ? s.filter(x => x !== p) : [...s, p]);
  const defaultsForRole = meta?.role_defaults?.[user?.role] || [];

  const save = async () => {
    try {
      const body = { permissions: selected };
      if (isSupervisor) body.group_ids = selectedGroups;
      await api.put(`/users/${user.id}`, body);
      toast.success("Permissões atualizadas");
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Shield size={18} className="text-primary" /> Permissões — {user.name}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          <p className="text-xs text-slate-500 mb-3">
            O perfil <b className="capitalize">{user.role}</b> já possui um conjunto padrão de permissões.
            Marque abaixo para conceder <b>permissões extras</b> a este usuário (permissões marcadas em cinza já vêm por perfil).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {meta?.all?.map(p => {
              const isDefault = defaultsForRole.includes(p);
              const checked = isDefault || selected.includes(p);
              return (
                <label key={p} className={`flex items-start gap-2 p-2 rounded-md border ${isDefault ? "bg-slate-50 border-slate-200 opacity-70" : "bg-white border-slate-200 hover:bg-slate-50"} transition-colors`}>
                  <Checkbox checked={checked} disabled={isDefault} onCheckedChange={() => toggle(p)} data-testid={`perm-${p}`} />
                  <div className="text-sm">
                    <div className="font-medium text-slate-900">{meta.labels[p] || p}</div>
                    <div className="text-xs text-slate-400 font-mono">{p}{isDefault && " · padrão do perfil"}</div>
                  </div>
                </label>
              );
            })}
          </div>

          {/* Só aparece para Supervisor — para os demais perfis, Grupo não
              controla nada (Admin já tem visão operacional completa; Analista
              não aprova fichas), então o controle não é exibido. */}
          {isSupervisor && (
            <div className="mt-5 pt-4 border-t border-slate-200">
              <div className="flex items-center gap-2 mb-1.5"><Layers size={15} className="text-primary" /><div className="font-heading font-semibold text-sm">Grupos permitidos</div></div>
              <p className="text-xs text-slate-500 mb-2">
                Sem nenhum Grupo selecionado, este supervisor não tem restrição — continua vendo e aprovando fichas de qualquer Produto/Sistema do tenant, como sempre. Selecionando um ou mais Grupos, ele passa a ver e aprovar só as fichas dos Produtos/Sistemas desses Grupos.
              </p>
              <GroupMultiSelect groups={groups} selected={selectedGroups} onChange={setSelectedGroups} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={save} data-testid="perm-save">Salvar permissões</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
